import { formatBRL, formatPct } from './format';
import type { SortState } from './tableFilter';

export interface AccountColumn {
    key: string;
    label: string;
}

const STATUS_LABELS: Record<string, string> = {
    ativa: 'Ativa',
    em_atraso: 'Em atraso',
    gratuita: 'Gratuita',
    suspensa: 'Suspensa',
    cancelada: 'Cancelada',
    reembolsada: 'Reembolsada',
    sem_assinatura: 'Sem assinatura',
};

const SETUP_LABELS: Record<string, string> = {
    atingiu: 'Sim',
    nao_atingiu: 'Não',
    em_observacao: 'Em observação',
};

const text = (map: Record<string, string>) => (v: unknown) =>
    typeof v === 'string' && v !== '' ? (map[v] ?? v) : '-';

export const formatSubscriptionStatus = text(STATUS_LABELS);
export const formatSetupSituation = text(SETUP_LABELS);

export const DEFAULT_ACCOUNT_SORT: SortState = { key: 'conta', dir: 'asc' };

/** Colunas de "Evolução das contas no setup proposto", como no prototipo. */
export const SETUP_COLUMNS: AccountColumn[] = [
    { key: 'conta', label: 'Cliente' },
    { key: 'email', label: 'E-mail' },
    { key: 'whatsapp', label: 'Telefone' },
    { key: 'assinatura', label: 'Assinatura' },
    { key: 'primeiroPagamento', label: '1º pagamento' },
    { key: 'lancamentos30d', label: 'Lançamentos até D30' },
    { key: 'diasComLancamento', label: 'Dias com lançamento' },
    { key: 'diasDeUso', label: 'Dias com acesso' },
    { key: 'situacao', label: 'Atingiu critério' },
];

export const SETUP_FORMATTERS = {
    assinatura: formatSubscriptionStatus,
    situacao: formatSetupSituation,
};

/** Colunas da lista "Pagou e nunca acessou": identificacao, assinatura e cadastro. */
export const NEVER_ACCESSED_COLUMNS: AccountColumn[] = [
    { key: 'conta', label: 'Cliente' },
    { key: 'email', label: 'E-mail' },
    { key: 'whatsapp', label: 'Telefone' },
    { key: 'assinatura', label: 'Assinatura' },
    { key: 'cadastro', label: 'Cadastro' },
];

export const NEVER_ACCESSED_FORMATTERS = {
    assinatura: formatSubscriptionStatus,
};

/** Colunas de "Movimentações por cliente", como no prototipo. */
export const MOVEMENTS_COLUMNS: AccountColumn[] = [
    { key: 'conta', label: 'Cliente' },
    { key: 'email', label: 'E-mail' },
    { key: 'whatsapp', label: 'Telefone' },
    { key: 'assinatura', label: 'Assinatura' },
    { key: 'lancamentos', label: 'Lançamentos' },
    { key: 'entradasQtd', label: 'Entradas (N)' },
    { key: 'entradasValor', label: 'Entradas (R$)' },
    { key: 'saidasQtd', label: 'Saídas (N)' },
    { key: 'saidasValor', label: 'Saídas (R$)' },
    { key: 'resultado', label: 'Resultado (R$)' },
];

const money = (v: unknown) => (typeof v === 'number' ? formatBRL(v) : '-');
const percent = (v: unknown) => (typeof v === 'number' ? formatPct(v) : '-');

export const MOVEMENTS_FORMATTERS = {
    assinatura: formatSubscriptionStatus,
    entradasValor: money,
    saidasValor: money,
    resultado: money,
};

/** Colunas de "Funcionalidades mais utilizadas", como no prototipo. */
export const MOST_USED_COLUMNS: AccountColumn[] = [
    { key: 'label', label: 'Funcionalidade / ação' },
    { key: 'contas', label: 'Contas' },
    { key: 'acoes', label: 'Ações' },
    { key: 'pagantes', label: 'Pagantes' },
    { key: 'adocaoPagantes', label: 'Adoção pagantes' },
    { key: 'gratuitas', label: 'Gratuitas' },
    { key: 'adocaoGratuitas', label: 'Adoção gratuitas' },
];

export const MOST_USED_FORMATTERS = {
    adocaoPagantes: percent,
    adocaoGratuitas: percent,
};

export const MOST_USED_SORT: SortState = { key: 'label', dir: 'asc' };

const PLAN_TYPE_LABELS: Record<string, string> = { monthly: 'Mensal', yearly: 'Anual', free: 'Gratuito' };
export const formatPlanType = text(PLAN_TYPE_LABELS);

const BILLING_LABELS: Record<string, string> = {
    em_dia: 'Em dia',
    proximo_vencimento: 'Próximo do vencimento',
    em_atraso: 'Em atraso',
    acesso_gratuito: 'Acesso gratuito',
    cancelada: 'Cancelada',
    reembolsada: 'Reembolsada',
};
export const formatBillingStatus = text(BILLING_LABELS);

/** Colunas de "Assinaturas por cliente" (Assinaturas > Geral, Receitas e Churn), como no prototipo. */
export const SUBSCRIPTIONS_COLUMNS: AccountColumn[] = [
    { key: 'conta', label: 'Cliente' },
    { key: 'email', label: 'E-mail' },
    { key: 'whatsapp', label: 'Telefone' },
    { key: 'segmento', label: 'Segmento' },
    { key: 'faturamento', label: 'Faturamento anual' },
    { key: 'assinatura', label: 'Assinatura' },
    { key: 'tipoPlano', label: 'Tipo de plano' },
    { key: 'primeiroPagamento', label: '1º pagamento' },
    { key: 'cicloAtual', label: 'Ciclo atual' },
    { key: 'inicioCiclo', label: 'Início do ciclo' },
    { key: 'ultimoPagamento', label: 'Último pagamento' },
    { key: 'vencimento', label: 'Próx. vencimento' },
    { key: 'renovacao', label: 'Próx. renovação' },
    { key: 'situacaoCobranca', label: 'Situação da cobrança' },
    { key: 'diasEmAtraso', label: 'Dias em atraso' },
    { key: 'mrrConta', label: 'MRR da conta' },
    { key: 'ltvPago', label: 'LTV pago' },
];

export const SUBSCRIPTIONS_FORMATTERS = {
    assinatura: formatSubscriptionStatus,
    tipoPlano: formatPlanType,
    situacaoCobranca: formatBillingStatus,
    mrrConta: money,
    ltvPago: money,
};

/** Colunas de "Contas na etapa" do funil, como no prototipo. */
export const FUNNEL_COLUMNS: AccountColumn[] = [
    { key: 'conta', label: 'Cliente' },
    { key: 'email', label: 'E-mail' },
    { key: 'assinatura', label: 'Assinatura' },
    { key: 'cadastro', label: 'Cadastro' },
    { key: 'ultimoAcesso', label: 'Último acesso' },
    { key: 'lancamentos', label: 'Lançamentos' },
    { key: 'diasDesdeCadastro', label: 'Dias desde cadastro' },
];

export const FUNNEL_FORMATTERS = {
    assinatura: formatSubscriptionStatus,
};

/** Colunas de "Respostas por cliente" (Mapeamento de clientes). */
export const ANSWERS_COLUMNS: AccountColumn[] = [
    { key: 'conta', label: 'Cliente' },
    { key: 'email', label: 'E-mail' },
    { key: 'whatsapp', label: 'Telefone' },
    { key: 'assinatura', label: 'Assinatura' },
    { key: 'cadastro', label: 'Cadastro' },
    { key: 'respondeuEm', label: 'Respondeu em' },
    { key: 'segmento', label: 'Segmento' },
    { key: 'setor', label: 'Setor' },
    { key: 'controle', label: 'Controle informado' },
    { key: 'faturamento', label: 'Faturamento anual' },
    { key: 'funcionarios', label: 'Funcionários' },
    { key: 'objetivos', label: 'Objetivos' },
    { key: 'comoConheceu', label: 'Como conheceu' },
];

export const ANSWERS_FORMATTERS = {
    assinatura: formatSubscriptionStatus,
};

/** Tabelas locais do Mapeamento ("Ver dados e exportar"): percentuais com uma casa. */
export const PERCENT_FORMATTER = percent;
